/**
 * Dark platform section — headline + rotating hologram visual.
 */
import { ArrowRight } from 'lucide-react';

function PlatformHologram() {
    return (
        <div className="relative mx-auto w-full max-w-[min(100%,22rem)] sm:max-w-[26rem] aspect-square">
            {/* Ambient glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-[8%] rounded-full bg-brand-teal/20 blur-3xl"
            />

            {/* Rotating orbit ring */}
            <div className="lp-platform-orbit absolute inset-0" aria-hidden>
                <svg viewBox="0 0 400 400" className="h-full w-full" fill="none">
                    <defs>
                        <linearGradient id="lp-orbit-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3dbfb0" stopOpacity="0.9" />
                            <stop offset="50%" stopColor="#00B09B" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="#3dbfb0" stopOpacity="0.9" />
                        </linearGradient>
                    </defs>
                    <circle
                        cx="200" cy="200" r="188"
                        stroke="url(#lp-orbit-grad)"
                        strokeWidth="1.5"
                        opacity="0.55"
                    />
                    <circle
                        cx="200" cy="200" r="172"
                        stroke="#3dbfb0"
                        strokeWidth="1"
                        strokeDasharray="3 10"
                        opacity="0.35"
                    />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
                        const rad = (deg * Math.PI) / 180;
                        const x = 200 + 188 * Math.cos(rad);
                        const y = 200 + 188 * Math.sin(rad);
                        return (
                            <circle
                                key={deg}
                                cx={x}
                                cy={y}
                                r={deg % 90 === 0 ? 4 : 2.5}
                                fill={deg % 90 === 0 ? '#3dbfb0' : '#1a9a88'}
                                opacity={deg % 90 === 0 ? 0.95 : 0.65}
                            />
                        );
                    })}
                    {[30, 120, 210, 300].map(deg => {
                        const rad = (deg * Math.PI) / 180;
                        const x = 200 + 172 * Math.cos(rad);
                        const y = 200 + 172 * Math.sin(rad);
                        return (
                            <circle key={`inner-${deg}`} cx={x} cy={y} r="2" fill="#7dd9cc" opacity="0.5" />
                        );
                    })}
                </svg>
            </div>

            {/* Static figure — no rotation */}
            <div className="absolute inset-[14%] sm:inset-[12%]">
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full p-[4%] ring-1 ring-brand-teal/25 shadow-[0_0_60px_-10px_rgba(61,191,176,0.45)]">
                    <img
                        src="/images/person.png"
                        alt="AGEON longevity body scan"
                        className="lp-platform-figure-img h-[96%] w-full object-contain object-center"
                        draggable={false}
                    />
                </div>
                <div
                    aria-hidden
                    className="pointer-events-none absolute -bottom-[6%] left-1/2 h-8 w-[55%] -translate-x-1/2 rounded-[100%] bg-brand-teal/25 blur-xl"
                />
            </div>

            {/* Static inner highlight ring */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-[11%] rounded-full ring-1 ring-brand-teal/15"
            />
        </div>
    );
}

export default function LandingPlatform() {
    return (
        <section id="platform" className="relative scroll-mt-24 overflow-hidden landing-mesh-dark">
            <div aria-hidden className="pointer-events-none absolute inset-0 landing-grain opacity-25" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <div data-aos="fade-right">
                        <span className="lp-chip lp-chip--invert mb-6">The platform</span>
                        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-[2.75rem] leading-[2rem] text-white tracking-tight">
                            India&rsquo;s first premium longevity &amp; recovery platform
                        </h2>
                        <p className="mt-6 text-white/65 leading-relaxed text-lg max-w-lg">
                            AGEON combines cutting-edge diagnostics with evidence-based therapies
                            to deliver measurable improvements in health, recovery, and longevity.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href="https://ageon.in"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-teal-700"
                            >
                                Book assessment
                            </a>
                            <a
                                href="#membership"
                                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
                            >
                                View membership <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    <div className="relative flex items-center justify-center" data-aos="fade-left" data-aos-delay="100">
                        <PlatformHologram />
                    </div>
                </div>
            </div>
        </section>
    );
}
